// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — the Situation room's operator console (docs/AYNI_XR_CONTROL_ROOM_PLAN.md),
 * a side panel that pushes the globe aside like Browse, Tools and Analyze:
 *
 * - **Data age** (CR2): every globe's stream with how old its data is, on
 *   time, late or stale, and the clock in UTC and local time;
 * - **The board** (CR0, CR1): show it on the globes, play it, change it in
 *   the Khipu editor, go back to the default;
 * - **Briefing** (CR3): start the 72-hour briefing (or the board) recorded,
 *   with or without the briefer's name; finish it into a record to save,
 *   download or print as a pack; open a meeting, a second screen, and put
 *   the meeting on the walls;
 * - **Wall displays** (CR1): pair one by the code it shows, rename, unpair;
 * - **Own layers** (CR5): add GeoJSON, KML or KMZ, show or hide, rename,
 *   remove.
 *
 * Plans: the Situation Room plans. Every string set as text, never parsed.
 */

import { getLocale, plural, t } from '../i18n'
import { formatDate } from '../i18n/format'
import type { Khipu } from '../services/khipu'
import { localized } from '../services/khipu'
import type { BriefingResult, BriefingSession } from '../services/briefingSession'
import type { OwnLayersController } from '../services/ownLayers'
import { readOwnLayerFile } from '../services/ownLayerFile'
import {
  claimDisplay, clearBoard, clearBriefing, deleteLayer, loadSituation, putBriefing, renameDisplay, renameLayer, saveBoard,
  unpairDisplay, uploadLayer, type SituationError,
} from '../services/situationClient'
import { formatPairCode, normalizePairCode, type SituationState } from '../services/situationRoom'
import { ageParts, worstFreshness, type FreshnessLevel } from '../services/streamFreshness'
import { briefingTemplate, defaultBoard } from '../services/situationTemplates'
import { escapeAttr, escapeHtml } from './domUtils'

export interface FreshnessRow {
  /** Globe number, from 1. */
  globe: number
  title: string
  level: FreshnessLevel | null
  ageMs: number | null
  asOfMs: number | null
}

export interface SituationPanelHost {
  /** Put the board's first step on the globes (CR0). */
  showSituation(board: Khipu): Promise<void>
  /** Play the board in the Khipu player. */
  playBoard(board: Khipu): void
  /** Change the board in the Khipu editor; its "Save as the board" calls `save`. */
  editBoard(board: Khipu, save: (k: Khipu) => Promise<{ ok: boolean; text: string }>): void
  briefing: BriefingSession
  /** The second screen: the output window that follows this page. */
  openSecondScreen(): 'opened' | 'blocked'
  openMeeting(trigger: HTMLElement): void
  /** The meeting this page presents, if any: its code, audience link (a path) and end. */
  presentedMeeting(): { code: string; audience: string; expiresAt: number } | null
  freshness(): FreshnessRow[]
  ownLayers: OwnLayersController
  saveRecord(k: Khipu): Promise<{ ok: boolean; text: string }>
  downloadRecord(k: Khipu): Promise<void>
  printPack(r: BriefingResult): void
  /** Whether the account has a display name to put on a record. */
  canName(): boolean
  announce(message: string): void
}

const PANEL_ID = 'situation-panel'
const SIDE_PANEL_EVENT = 'ayni-side-panel'

let panel: HTMLElement | null = null
let host: SituationPanelHost | null = null
let trigger: HTMLElement | null = null
let state: SituationState | null = null
let loadError: SituationError | null = null
let status: { text: string; ok: boolean } | null = null
let pairStatus: { text: string; ok: boolean } | null = null
let layerStatus: { text: string; ok: boolean } | null = null
let withName = false
let clockTimer: number | null = null
let offs: Array<() => void> = []
/** Ids being renamed (an input shows instead of the name). */
const renaming = new Set<string>()

const announceSidePanel = (): void => { window.dispatchEvent(new Event(SIDE_PANEL_EVENT)) }

export function isSituationPanelOpen(): boolean {
  return panel !== null
}

function errorText(e: SituationError): string {
  switch (e) {
    case 'unauthorized': return t('situation.error.signIn')
    case 'plan_required': return t('situation.error.plan')
    case 'unavailable': return t('situation.error.unavailable')
    case 'not_found': return t('situation.error.notFound')
    case 'too_many': return t('situation.error.tooMany')
    case 'too_large': return t('situation.error.tooLarge')
    case 'too_many_features': return t('situation.error.tooManyFeatures')
    case 'empty': return t('situation.error.empty')
    case 'invalid': return t('situation.error.invalid')
    case 'rate_limited': return t('situation.error.rate')
    default: return t('situation.error.failed')
  }
}

const FRESH_KEY: Record<FreshnessLevel, 'situation.fresh.fresh' | 'situation.fresh.late' | 'situation.fresh.stale' | 'situation.fresh.unknown'> = {
  fresh: 'situation.fresh.fresh', late: 'situation.fresh.late', stale: 'situation.fresh.stale', unknown: 'situation.fresh.unknown',
}

/** "3 h old" (or minutes, days). */
export function ageText(ageMs: number): string {
  const { n, unit } = ageParts(ageMs)
  return plural(n, unit === 'minutes'
    ? { one: 'situation.age.minutes.one', other: 'situation.age.minutes.other' }
    : unit === 'hours' ? { one: 'situation.age.hours.one', other: 'situation.age.hours.other' } : { one: 'situation.age.days.one', other: 'situation.age.days.other' })
}

/** The clock line: UTC and local. */
export function clockText(now: Date = new Date()): string {
  const utc = now.toISOString().slice(11, 16)
  const local = formatDate(now, { hour: '2-digit', minute: '2-digit' })
  return t('situation.clock', { utc, local })
}

function board(): Khipu {
  return state?.board ?? defaultBoard()
}

function freshnessHtml(): string {
  const rows = host?.freshness() ?? []
  if (!rows.length) return `<p class="sr-muted">${escapeHtml(t('situation.fresh.none'))}</p>`
  const worst = worstFreshness(rows.map((r) => r.level).filter((l): l is FreshnessLevel => l !== null))
  const summary = worst === 'stale' ? 'situation.fresh.summary.stale' : worst === 'late' ? 'situation.fresh.summary.late' : worst === 'fresh' ? 'situation.fresh.summary.fresh' : 'situation.fresh.summary.unknown'
  return `<p class="sr-summary sr-level-${escapeAttr(worst ?? 'none')}">${escapeHtml(t(summary))}</p>
    <ul class="sr-list sr-fresh">${rows.map((r) => `<li>
      <span class="sr-globe" aria-hidden="true">${r.globe}</span>
      <span class="sr-name">${escapeHtml(r.title)}</span>
      ${r.level ? `<span class="sr-chip sr-level-${escapeAttr(r.level)}">${escapeHtml(t(FRESH_KEY[r.level]))}${r.ageMs !== null ? ` · ${escapeHtml(ageText(r.ageMs))}` : ''}</span>` : `<span class="sr-chip">${escapeHtml(t('situation.fresh.archive'))}</span>`}
    </li>`).join('')}</ul>`
}

function boardHtml(): string {
  const b = board()
  const own = state?.board ? t('situation.board.changed', { date: formatDate(new Date(state.boardUpdatedAt), { dateStyle: 'medium', timeStyle: 'short' }) }) : t('situation.board.default')
  return `<p class="sr-muted">${escapeHtml(localized(b.title, getLocale()))} · ${escapeHtml(plural(b.path.steps.length, { one: 'situation.board.steps.one', other: 'situation.board.steps.other' }))} · ${escapeHtml(own)}</p>
    <div class="sr-row">
      <button type="button" class="sr-btn sr-primary" data-act="show">${escapeHtml(t('situation.board.show'))}</button>
      <button type="button" class="sr-btn" data-act="play">${escapeHtml(t('situation.board.play'))}</button>
      <button type="button" class="sr-btn" data-act="edit-board"${state ? '' : ' disabled'}>${escapeHtml(t('situation.board.edit'))}</button>
      ${state?.board ? `<button type="button" class="sr-btn sr-quiet" data-act="reset-board">${escapeHtml(t('situation.board.reset'))}</button>` : ''}
    </div>`
}

function resultHtml(r: BriefingResult): string {
  return `<div class="sr-result">
    <p><strong>${escapeHtml(r.title)}</strong></p>
    <p class="sr-muted">${escapeHtml(plural(r.steps.length, { one: 'situation.briefing.shown.one', other: 'situation.briefing.shown.other' }))} · ${escapeHtml(plural(r.decisions.length, { one: 'situation.briefing.decisions.one', other: 'situation.briefing.decisions.other' }))}${r.who ? ` · ${escapeHtml(t('situation.pack.by', { name: r.who }))}` : ''}</p>
    <div class="sr-row">
      <button type="button" class="sr-btn sr-primary" data-act="save-record">${escapeHtml(t('situation.briefing.save'))}</button>
      <button type="button" class="sr-btn" data-act="pack">${escapeHtml(t('situation.briefing.pack'))}</button>
      <button type="button" class="sr-btn" data-act="download-record">${escapeHtml(t('situation.briefing.download'))}</button>
    </div>
  </div>`
}

function briefingHtml(): string {
  const b = host?.briefing
  const meeting = host?.presentedMeeting() ?? null
  const onWall = state?.briefing && state.briefing.until > Date.now() ? state.briefing : null
  const progress = b?.progress()
  const last = b?.last()
  const running = progress
    ? `<p class="sr-recording" role="status"><span class="sr-dot" aria-hidden="true"></span> ${escapeHtml(t('situation.briefing.recording', { step: progress.step, total: progress.total }))}</p>
       <div class="sr-row">
         <button type="button" class="sr-btn sr-primary" data-act="finish">${escapeHtml(t('situation.briefing.finish'))}</button>
         <button type="button" class="sr-btn sr-quiet" data-act="discard">${escapeHtml(t('situation.briefing.discard'))}</button>
       </div>`
    : `<label class="sr-field"><span>${escapeHtml(t('situation.briefing.which'))}</span>
         <select class="sr-template">
           <option value="briefing">${escapeHtml(t('situation.briefing.template'))}</option>
           <option value="board">${escapeHtml(t('situation.briefing.board'))}</option>
         </select></label>
       <label class="sr-check"><input type="checkbox" class="sr-with-name"${withName ? ' checked' : ''}${host?.canName() ? '' : ' disabled'}> <span>${escapeHtml(t('situation.briefing.withName'))}</span></label>
       <p class="sr-muted sr-small">${escapeHtml(t(withName ? 'situation.briefing.withNameNotice' : 'situation.briefing.anonymousNotice'))}</p>
       <div class="sr-row"><button type="button" class="sr-btn sr-primary" data-act="start">${escapeHtml(t('situation.briefing.start'))}</button></div>`
  return `${running}
    ${!progress && last ? resultHtml(last) : ''}
    <div class="sr-row">
      <button type="button" class="sr-btn" data-act="meeting">${escapeHtml(t(meeting ? 'situation.briefing.meetingOn' : 'situation.briefing.meeting'))}</button>
      <button type="button" class="sr-btn" data-act="second">${escapeHtml(t('situation.briefing.second'))}</button>
    </div>
    ${meeting
      ? `<div class="sr-row">${onWall && onWall.code === meeting.code
          ? `<button type="button" class="sr-btn" data-act="wall-off">${escapeHtml(t('situation.briefing.wallOff'))}</button>`
          : `<button type="button" class="sr-btn" data-act="wall-on"${state?.displays.length ? '' : ' disabled'}>${escapeHtml(t('situation.briefing.wallOn'))}</button>`}</div>`
      : onWall ? `<div class="sr-row"><button type="button" class="sr-btn" data-act="wall-off">${escapeHtml(t('situation.briefing.wallOff'))}</button></div>` : ''}`
}

function seenText(ms: number): string {
  if (!ms) return t('situation.walls.never')
  return t('situation.walls.seen', { age: ageText(Math.max(0, Date.now() - ms)) })
}

function wallsHtml(): string {
  if (!state) return ''
  const list = state.displays.length
    ? `<ul class="sr-list">${state.displays.map((d) => `<li data-id="${escapeAttr(d.id)}">
        ${renaming.has(d.id)
          ? `<input type="text" class="sr-rename" data-kind="display" maxlength="60" value="${escapeAttr(d.name)}" aria-label="${escapeAttr(t('situation.walls.name'))}">`
          : `<span class="sr-name">${escapeHtml(d.name)}</span><span class="sr-muted sr-small">${escapeHtml(seenText(d.lastSeenAt))}</span>`}
        <span class="sr-actions">
          <button type="button" class="sr-btn sr-small-btn" data-act="${renaming.has(d.id) ? 'rename-done' : 'rename'}" data-kind="display">${escapeHtml(t(renaming.has(d.id) ? 'situation.done' : 'situation.rename'))}</button>
          <button type="button" class="sr-btn sr-small-btn sr-quiet" data-act="unpair">${escapeHtml(t('situation.walls.unpair'))}</button>
        </span></li>`).join('')}</ul>`
    : `<p class="sr-muted">${escapeHtml(t('situation.walls.none'))}</p>`
  const full = state.displays.length >= state.limits.displays
  return `${list}
    <p class="sr-muted sr-small">${escapeHtml(t('situation.walls.how', { url: `${location.origin}/?wall=1` }))}</p>
    <div class="sr-pair">
      <label class="sr-field sr-field-code"><span>${escapeHtml(t('situation.walls.code'))}</span><input type="text" class="sr-code" maxlength="9" autocomplete="off" spellcheck="false" placeholder="ABC-234"${full ? ' disabled' : ''}></label>
      <label class="sr-field"><span>${escapeHtml(t('situation.walls.name'))}</span><input type="text" class="sr-wall-name" maxlength="60" placeholder="${escapeAttr(t('situation.walls.namePlaceholder'))}"${full ? ' disabled' : ''}></label>
      <button type="button" class="sr-btn sr-primary" data-act="pair"${full ? ' disabled' : ''}>${escapeHtml(t('situation.walls.pair'))}</button>
    </div>
    ${full ? `<p class="sr-muted sr-small">${escapeHtml(t('situation.walls.full', { n: state.limits.displays }))}</p>` : ''}
    ${pairStatus ? `<p class="sr-status${pairStatus.ok ? '' : ' sr-error'}" role="status">${escapeHtml(pairStatus.text)}</p>` : ''}`
}

function layersHtml(): string {
  if (!state || !host) return ''
  const own = host.ownLayers
  const list = state.layers.length
    ? `<ul class="sr-list">${state.layers.map((l) => `<li data-id="${escapeAttr(l.id)}">
        <label class="sr-check sr-layer"><input type="checkbox" class="sr-layer-shown"${own.shown(l.id) ? ' checked' : ''}>
          <span class="sr-swatch" style="background:${escapeAttr(own.colour(l.id))}" aria-hidden="true"></span>
          ${renaming.has(l.id)
            ? `<input type="text" class="sr-rename" data-kind="layer" maxlength="80" value="${escapeAttr(l.title)}" aria-label="${escapeAttr(t('situation.layers.title'))}">`
            : `<span class="sr-name">${escapeHtml(l.title)}</span>`}
        </label>
        <span class="sr-muted sr-small">${escapeHtml(plural(l.features, { one: 'situation.layers.features.one', other: 'situation.layers.features.other' }))}${own.failed(l.id) ? ` · ${escapeHtml(t('situation.layers.failed'))}` : ''}</span>
        <span class="sr-actions">
          <button type="button" class="sr-btn sr-small-btn" data-act="${renaming.has(l.id) ? 'rename-done' : 'rename'}" data-kind="layer">${escapeHtml(t(renaming.has(l.id) ? 'situation.done' : 'situation.rename'))}</button>
          <button type="button" class="sr-btn sr-small-btn sr-quiet" data-act="delete-layer">${escapeHtml(t('situation.layers.delete'))}</button>
        </span></li>`).join('')}</ul>`
    : `<p class="sr-muted">${escapeHtml(t('situation.layers.none'))}</p>`
  const full = state.layers.length >= state.limits.layers
  return `${list}
    <label class="sr-btn sr-file${full ? ' sr-disabled' : ''}"><input type="file" class="sr-layer-file" accept=".geojson,.json,.kml,.kmz,application/geo+json,application/vnd.google-earth.kml+xml,application/vnd.google-earth.kmz"${full ? ' disabled' : ''}> ${escapeHtml(t('situation.layers.add'))}</label>
    <p class="sr-muted sr-small">${escapeHtml(t('situation.layers.how', { n: state.limits.layers, mb: (state.limits.layerBytes / 1_000_000).toFixed(1) }))}</p>
    ${layerStatus ? `<p class="sr-status${layerStatus.ok ? '' : ' sr-error'}" role="status">${escapeHtml(layerStatus.text)}</p>` : ''}`
}

function render(): void {
  if (!panel || !host) return
  // Keep a half-typed pairing code and wall name across a redraw.
  const code = panel.querySelector<HTMLInputElement>('.sr-code')?.value ?? ''
  const wallName = panel.querySelector<HTMLInputElement>('.sr-wall-name')?.value ?? ''
  const template = panel.querySelector<HTMLSelectElement>('.sr-template')?.value ?? 'briefing'
  const focusedAct = (document.activeElement as HTMLElement | null)?.closest?.(`#${PANEL_ID} [data-act]`)?.getAttribute('data-act') ?? null
  const body = !state
    ? `<p class="sr-status${loadError ? ' sr-error' : ''}" role="status">${escapeHtml(loadError ? errorText(loadError) : t('situation.loading'))}</p>
       ${loadError && loadError !== 'plan_required' && loadError !== 'unauthorized' ? `<button type="button" class="sr-btn" data-act="reload">${escapeHtml(t('situation.reload'))}</button>` : ''}`
    : ''
  panel.innerHTML = `
    <header class="sr-header">
      <h2 class="sr-title" id="situation-title">${escapeHtml(t('situation.title'))}</h2>
      <button type="button" class="sr-close" aria-label="${escapeAttr(t('situation.close'))}">✕</button>
    </header>
    <p class="sr-clock" aria-live="off">${escapeHtml(clockText())}</p>
    ${status ? `<p class="sr-status${status.ok ? '' : ' sr-error'}" role="status">${escapeHtml(status.text)}</p>` : ''}
    <section class="sr-section" aria-labelledby="sr-h-fresh"><h3 id="sr-h-fresh">${escapeHtml(t('situation.fresh.title'))}</h3>${freshnessHtml()}</section>
    <section class="sr-section" aria-labelledby="sr-h-board"><h3 id="sr-h-board">${escapeHtml(t('situation.board.title'))}</h3>${boardHtml()}</section>
    <section class="sr-section" aria-labelledby="sr-h-briefing"><h3 id="sr-h-briefing">${escapeHtml(t('situation.briefing.title'))}</h3>${briefingHtml()}</section>
    ${body}
    ${state ? `<section class="sr-section" aria-labelledby="sr-h-walls"><h3 id="sr-h-walls">${escapeHtml(t('situation.walls.title'))}</h3>${wallsHtml()}</section>
    <section class="sr-section" aria-labelledby="sr-h-layers"><h3 id="sr-h-layers">${escapeHtml(t('situation.layers.title'))}</h3>${layersHtml()}</section>` : ''}`
  const codeInput = panel.querySelector<HTMLInputElement>('.sr-code')
  if (codeInput) codeInput.value = code
  const nameInput = panel.querySelector<HTMLInputElement>('.sr-wall-name')
  if (nameInput) nameInput.value = wallName
  const select = panel.querySelector<HTMLSelectElement>('.sr-template')
  if (select) select.value = template
  if (focusedAct) panel.querySelector<HTMLElement>(`[data-act="${focusedAct}"]`)?.focus({ preventScroll: true })
  panel.querySelector<HTMLInputElement>('.sr-rename')?.focus({ preventScroll: true })
}

async function reload(): Promise<void> {
  const res = await loadSituation()
  if (!panel) return
  if (res.ok) {
    state = res.body
    loadError = null
    host?.ownLayers.setList(state.layers, { kind: 'member' })
  } else {
    loadError = res.error
  }
  render()
}

function say(text: string, ok = true): void {
  status = { text, ok }
  render()
  host?.announce(text)
}

async function pair(): Promise<void> {
  if (!panel) return
  const code = normalizePairCode(panel.querySelector<HTMLInputElement>('.sr-code')?.value ?? '')
  const name = (panel.querySelector<HTMLInputElement>('.sr-wall-name')?.value ?? '').trim() || t('situation.walls.defaultName', { n: (state?.displays.length ?? 0) + 1 })
  if (!code) {
    pairStatus = { text: t('situation.walls.badCode'), ok: false }
    render()
    return
  }
  pairStatus = { text: t('situation.walls.pairing', { code: formatPairCode(code) }), ok: true }
  render()
  const res = await claimDisplay(code, name)
  pairStatus = res.ok ? { text: t('situation.walls.paired', { name }), ok: true } : { text: res.error === 'not_found' ? t('situation.walls.unknownCode') : errorText(res.error), ok: false }
  if (res.ok) {
    const code = panel.querySelector<HTMLInputElement>('.sr-code')
    const nameInput = panel.querySelector<HTMLInputElement>('.sr-wall-name')
    if (code) code.value = ''
    if (nameInput) nameInput.value = ''
  }
  host?.announce(pairStatus.text)
  await reload()
}

async function addLayer(input: HTMLInputElement): Promise<void> {
  const file = input.files?.[0]
  input.value = ''
  if (!file) return
  layerStatus = { text: t('situation.layers.reading', { name: file.name }), ok: true }
  render()
  const read = await readOwnLayerFile(file)
  if (!read.ok) {
    const e = read.error
    layerStatus = { text: e === 'not_supported' ? t('situation.layers.notSupported') : e === 'unreadable' ? t('situation.layers.unreadable') : errorText(e), ok: false }
    render()
    return
  }
  const res = await uploadLayer(read.title, read.geojson)
  layerStatus = res.ok
    ? { text: t('situation.layers.added', { title: read.title, n: res.body.features }), ok: true }
    : { text: errorText(res.error), ok: false }
  host?.announce(layerStatus.text)
  await reload()
}

async function finishRename(li: HTMLElement, kind: string): Promise<void> {
  const id = li.dataset.id ?? ''
  const value = li.querySelector<HTMLInputElement>('.sr-rename')?.value.trim() ?? ''
  renaming.delete(id)
  if (value) {
    const res = kind === 'layer' ? await renameLayer(id, value) : await renameDisplay(id, value)
    if (!res.ok) say(errorText(res.error), false)
  }
  await reload()
}

async function onClick(e: Event): Promise<void> {
  const button = (e.target as HTMLElement).closest<HTMLElement>('button')
  if (!button || !host || !panel) return
  if (button.classList.contains('sr-close')) return closeSituationPanel()
  const act = button.dataset.act
  const li = button.closest<HTMLElement>('li')
  switch (act) {
    case 'reload':
      loadError = null
      render()
      return void reload()
    case 'show':
      await host.showSituation(board())
      return say(t('situation.board.shown'))
    case 'play':
      return host.playBoard(board())
    case 'edit-board':
      return host.editBoard(board(), async (k) => {
        const res = await saveBoard(k)
        void reload()
        return res.ok ? { ok: true, text: t('situation.board.saved') } : { ok: false, text: errorText(res.error) }
      })
    case 'reset-board': {
      const res = await clearBoard()
      if (!res.ok) return say(errorText(res.error), false)
      await reload()
      return say(t('situation.board.resetDone'))
    }
    case 'start': {
      const which = panel.querySelector<HTMLSelectElement>('.sr-template')?.value
      host.briefing.start(which === 'board' ? board() : briefingTemplate(), { attributed: withName && host.canName() })
      return
    }
    case 'finish': {
      const r = await host.briefing.finish()
      if (r) say(t('situation.briefing.finished', { n: r.steps.length }))
      return
    }
    case 'discard':
      host.briefing.discard()
      return say(t('situation.briefing.discarded'))
    case 'save-record': {
      const r = host.briefing.last()
      if (!r) return
      const res = await host.saveRecord(r.record)
      return say(res.text, res.ok)
    }
    case 'pack': {
      const r = host.briefing.last()
      if (r) host.printPack(r)
      return
    }
    case 'download-record': {
      const r = host.briefing.last()
      if (r) await host.downloadRecord(r.record)
      return
    }
    case 'meeting':
      return host.openMeeting(button)
    case 'second':
      if (host.openSecondScreen() === 'blocked') say(t('situation.briefing.secondBlocked'), false)
      return
    case 'wall-on': {
      const m = host.presentedMeeting()
      if (!m) return
      const res = await putBriefing({ code: m.code, audience: m.audience, until: m.expiresAt })
      await reload()
      return say(res.ok ? t('situation.briefing.wallOnDone') : errorText(res.error), res.ok)
    }
    case 'wall-off': {
      const res = await clearBriefing()
      await reload()
      return say(res.ok ? t('situation.briefing.wallOffDone') : errorText(res.error), res.ok)
    }
    case 'pair':
      return void pair()
    case 'rename':
      if (li?.dataset.id) renaming.add(li.dataset.id)
      return render()
    case 'rename-done':
      if (li) return void finishRename(li, button.dataset.kind ?? '')
      return
    case 'unpair': {
      if (!li?.dataset.id) return
      const res = await unpairDisplay(li.dataset.id)
      pairStatus = res.ok ? { text: t('situation.walls.unpaired'), ok: true } : { text: errorText(res.error), ok: false }
      return void reload()
    }
    case 'delete-layer': {
      if (!li?.dataset.id) return
      const res = await deleteLayer(li.dataset.id)
      layerStatus = res.ok ? { text: t('situation.layers.deleted'), ok: true } : { text: errorText(res.error), ok: false }
      return void reload()
    }
  }
}

function onChange(e: Event): void {
  const target = e.target as HTMLInputElement
  if (target.classList.contains('sr-with-name')) {
    withName = target.checked
    render()
  } else if (target.classList.contains('sr-layer-shown')) {
    const id = target.closest<HTMLElement>('li')?.dataset.id
    if (id) host?.ownLayers.setShown(id, target.checked)
  } else if (target.classList.contains('sr-layer-file')) {
    void addLayer(target)
  }
}

function onKey(e: KeyboardEvent): void {
  if (!panel) return
  const target = e.target as HTMLElement
  if (e.key === 'Enter' && target.classList.contains('sr-rename')) {
    e.preventDefault()
    const li = target.closest<HTMLElement>('li')
    if (li) void finishRename(li, target.dataset.kind ?? '')
    return
  }
  if (e.key === 'Enter' && (target.classList.contains('sr-code') || target.classList.contains('sr-wall-name'))) {
    e.preventDefault()
    void pair()
    return
  }
  if (e.key === 'Escape' && panel.contains(target) && target.classList.contains('sr-rename')) {
    const id = target.closest<HTMLElement>('li')?.dataset.id
    if (id) renaming.delete(id)
    e.stopPropagation()
    render()
  }
}

/** Escape closes the panel when the focus is in it or on nothing in particular (as the Studio's). */
function onWindowKey(e: KeyboardEvent): void {
  if (e.key !== 'Escape' || !panel) return
  const target = e.target as HTMLElement
  const inPanel = panel.contains(target)
  if (!inPanel && target !== document.body && target !== document.documentElement) return
  if (inPanel && target.classList.contains('sr-rename')) return
  e.stopPropagation()
  closeSituationPanel()
}

/** The panel's foot clears the transport and the Tools bar, like the Studio's. */
function positionPanel(): void {
  if (!panel) return
  let clearance = 0
  for (const id of ['playback-controls', 'map-controls']) {
    const el = document.getElementById(id)
    if (!el || el.classList.contains('hidden')) continue
    const rect = el.getBoundingClientRect()
    if (rect.height > 0) clearance = Math.max(clearance, window.innerHeight - rect.top)
  }
  panel.style.insetBlockEnd = clearance > 0 && window.innerWidth > 600 ? `${clearance + 8}px` : ''
}

export function openSituationPanel(panelHost: SituationPanelHost, from: HTMLElement | null = null): void {
  closeSituationPanel(false)
  host = panelHost
  trigger = from
  status = null
  pairStatus = null
  layerStatus = null
  panel = document.createElement('section')
  panel.id = PANEL_ID
  panel.className = 'situation-panel'
  panel.setAttribute('role', 'dialog')
  panel.setAttribute('aria-labelledby', 'situation-title')
  document.body.appendChild(panel)
  panel.addEventListener('click', (e) => void onClick(e))
  panel.addEventListener('change', onChange)
  panel.addEventListener('keydown', onKey)
  window.addEventListener('resize', positionPanel)
  window.addEventListener('keydown', onWindowKey, true)
  offs = [
    panelHost.briefing.onChange(render),
    panelHost.ownLayers.onChange(render),
  ]
  // The clock and the data's ages move on by themselves.
  clockTimer = window.setInterval(render, 30_000)
  render()
  positionPanel()
  announceSidePanel()
  void reload()
  panel.querySelector<HTMLElement>('[data-act="show"]')?.focus({ preventScroll: true })
}

/** Redraw (the globes changed, so their data's ages did). */
export function refreshSituationPanel(): void {
  // Not while someone types in it: the next change or the clock redraws.
  const typing = document.activeElement && panel?.contains(document.activeElement) && (document.activeElement as HTMLElement).matches('input[type="text"], textarea')
  if (!typing) render()
}

export function closeSituationPanel(animate = true): void {
  if (!panel) return
  if (clockTimer !== null) window.clearInterval(clockTimer)
  clockTimer = null
  for (const off of offs) off()
  offs = []
  window.removeEventListener('resize', positionPanel)
  window.removeEventListener('keydown', onWindowKey, true)
  renaming.clear()
  const el = panel
  const still = typeof window.matchMedia !== 'function' || window.matchMedia('(prefers-reduced-motion: reduce)').matches
  if (!animate || still) el.remove()
  else {
    el.removeAttribute('id')
    el.classList.add('situation-leaving')
    el.setAttribute('aria-hidden', 'true')
    el.inert = true
    window.setTimeout(() => el.remove(), 700)
  }
  panel = null
  announceSidePanel()
  host = null
  trigger?.focus()
  trigger = null
}
